import { useParams } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { Loader2 } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { useMenuContents } from '../hooks/use-menu-contents'
import { useUpdateSection } from '../hooks/use-section-mutations'
import { sortOrderForAppend, sortOrderForDrop } from '../lib/sort-order'
import {
  useAddItem,
  useMoveItem,
  useUpdateItem,
} from '../hooks/use-item-mutations'
import { MenuEditorHeader } from '../components/menu-editor-header'
import { MenuSectionCard } from '../components/menu-section-card'
import { ProductSelectorPanel } from '../components/product-selector-panel'
import { AddSectionDialog } from '../components/add-section-dialog'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'

export function MenuEditorPage() {
  const { t: tMenus } = useTranslation('menus')
  const { menuId } = useParams({ strict: false })
  const menuIdNum = menuId ? parseInt(menuId) : 0

  const [addSectionOpen, setAddSectionOpen] = useState(false)

  const { data: menuData, isLoading, error } = useMenuContents(menuIdNum)
  const updateSection = useUpdateSection()
  const updateItem = useUpdateItem()
  const addItem = useAddItem()
  const moveItem = useMoveItem()

  // Setup global drag and drop monitoring
  useEffect(() => {
    if (!menuData) return

    return monitorForElements({
      onDrop({ source, location }) {
        // Pragmatic-DnD orders dropTargets INNERMOST FIRST — its own docs spell it
        // `[grandChildRecord, childRecord, parentRecord]` — so the most specific target is [0].
        //
        // The editor used to read `[length - 1]` with a comment claiming that was the innermost.
        // It never showed, because until now an item drag only ever matched one target: the row.
        // Letting a section accept a re-filed item (CHG-2026-09-menu-editor-redesign) gave item
        // drags a second target, and the row drop started landing on the section instead — the
        // item moved, but always to the end, never where it was dropped.
        // Dropped outside every target — the monitor still fires. Checked by length because the
        // library types the index access as always present, so `!destination` reads as dead code.
        if (location.current.dropTargets.length === 0) return
        const destination = location.current.dropTargets[0]

        const sourceData = source.data
        const destinationData = destination.data

        // Handle section reordering
        if (
          sourceData.type === 'section' &&
          destinationData.type === 'section'
        ) {
          const sourceSectionId = sourceData.sectionId as number
          const destSectionId = destinationData.sectionId as number
          const edge = extractClosestEdge(destinationData)

          if (sourceSectionId === destSectionId) return

          // Find source and destination sections
          const sections = menuData.sections
          const sourceIndex = sections.findIndex(
            (s) => s.id === sourceSectionId,
          )
          const destIndex = sections.findIndex((s) => s.id === destSectionId)

          if (sourceIndex === -1 || destIndex === -1) return

          // Calculate new order based on edge
          let newSortOrder: number
          if (edge === 'top') {
            // Insert before destination
            if (destIndex === 0) {
              newSortOrder = Math.max(0, sections[destIndex].sortOrder - 1000)
            } else {
              newSortOrder = Math.floor(
                (sections[destIndex - 1].sortOrder +
                  sections[destIndex].sortOrder) /
                  2,
              )
            }
          } else {
            // Insert after destination (edge === 'bottom')
            if (destIndex === sections.length - 1) {
              newSortOrder = sections[destIndex].sortOrder + 1000
            } else {
              newSortOrder = Math.floor(
                (sections[destIndex].sortOrder +
                  sections[destIndex + 1].sortOrder) /
                  2,
              )
            }
          }

          updateSection.mutate({
            menuId: menuIdNum,
            sectionId: sourceSectionId,
            data: { sortOrder: newSortOrder },
          })
        }

        // An item dropped on another item — in its own section or another one
        // (CHG-2026-09-menu-editor-redesign; a cross-section drop used to return here and do
        // nothing at all, which read as the editor ignoring the gesture).
        if (sourceData.type === 'item' && destinationData.type === 'item') {
          const sourceSectionId = sourceData.sectionId as number
          const destSectionId = destinationData.sectionId as number
          const sourceProductId = sourceData.productId as number
          const destProductId = destinationData.productId as number
          // The row only registers top/bottom as allowed edges, but the hitbox type covers all
          // four; narrow it rather than widening the ordering helper to edges it cannot mean.
          const rawEdge = extractClosestEdge(destinationData)
          const edge: 'top' | 'bottom' = rawEdge === 'top' ? 'top' : 'bottom'

          if (sourceProductId === destProductId) return

          const destSection = menuData.sections.find(
            (s) => s.id === destSectionId,
          )
          if (!destSection) return

          // Within one section the moved row is its own neighbour, and the midpoint between it
          // and the target would be the spot it already occupies. Drop it from the list first.
          const neighbours = destSection.items
            .filter(
              (i) =>
                !(
                  sourceSectionId === destSectionId &&
                  i.productId === sourceProductId
                ),
            )
            .map((i) => i.sortOrder)
          const destIndex = destSection.items
            .filter(
              (i) =>
                !(
                  sourceSectionId === destSectionId &&
                  i.productId === sourceProductId
                ),
            )
            .findIndex((i) => i.productId === destProductId)
          if (destIndex === -1) return

          const newSortOrder = sortOrderForDrop(neighbours, destIndex, edge)

          if (sourceSectionId === destSectionId) {
            updateItem.mutate({
              menuId: menuIdNum,
              sectionId: sourceSectionId,
              productId: sourceProductId,
              data: { sortOrder: newSortOrder },
            })
          } else {
            moveItem.mutate({
              menuId: menuIdNum,
              sectionId: sourceSectionId,
              productId: sourceProductId,
              toSectionId: destSectionId,
              sortOrder: newSortOrder,
            })
          }
          return
        }

        // An item dropped on a section's background or on an empty section: re-file it at the end.
        if (sourceData.type === 'item' && destinationData.type === 'section') {
          const sourceSectionId = sourceData.sectionId as number
          const destSectionId = destinationData.sectionId as number
          const sourceProductId = sourceData.productId as number
          if (sourceSectionId === destSectionId) return

          const destSection = menuData.sections.find(
            (s) => s.id === destSectionId,
          )
          if (!destSection) return

          moveItem.mutate({
            menuId: menuIdNum,
            sectionId: sourceSectionId,
            productId: sourceProductId,
            toSectionId: destSectionId,
            sortOrder: sortOrderForAppend(
              destSection.items.map((i) => i.sortOrder),
            ),
          })
          return
        }

        // Handle product drop into section or onto specific item
        if (sourceData.type === 'product') {
          const productId = sourceData.productId as number
          let sectionId: number
          let sortOrder: number

          // Check if dropped on an item (for precise positioning) or section (for end of list)
          if (destinationData.type === 'item') {
            // Dropped on a specific item - insert at that position
            sectionId = destinationData.sectionId as number
            const targetProductId = destinationData.productId as number
            const edge = extractClosestEdge(destinationData)

            const section = menuData.sections.find((s) => s.id === sectionId)
            if (!section) return

            const alreadyExists = section.items.some(
              (item) => item.productId === productId,
            )
            if (alreadyExists) return

            const items = section.items
            const targetIndex = items.findIndex(
              (i) => i.productId === targetProductId,
            )
            if (targetIndex === -1) return

            // Calculate sortOrder based on edge
            if (edge === 'top') {
              // Insert before target
              if (targetIndex === 0) {
                sortOrder = Math.max(0, items[targetIndex].sortOrder - 1000)
              } else {
                sortOrder = Math.floor(
                  (items[targetIndex - 1].sortOrder +
                    items[targetIndex].sortOrder) /
                    2,
                )
              }
            } else {
              // Insert after target (edge === 'bottom')
              if (targetIndex === items.length - 1) {
                sortOrder = items[targetIndex].sortOrder + 1000
              } else {
                sortOrder = Math.floor(
                  (items[targetIndex].sortOrder +
                    items[targetIndex + 1].sortOrder) /
                    2,
                )
              }
            }
          } else if (destinationData.type === 'section') {
            // Dropped on empty section or section background - add to end
            sectionId = destinationData.sectionId as number

            const section = menuData.sections.find((s) => s.id === sectionId)
            if (!section) return

            const alreadyExists = section.items.some(
              (item) => item.productId === productId,
            )
            if (alreadyExists) return

            // Add to the end of the list
            const maxSortOrder =
              section.items.length > 0
                ? Math.max(...section.items.map((i) => i.sortOrder))
                : 0
            sortOrder = maxSortOrder + 1000
          } else {
            return
          }

          addItem.mutate({
            menuId: menuIdNum,
            sectionId,
            data: {
              productId,
              sortOrder,
            },
          })
        }
      },
    })
  }, [menuData, menuIdNum, updateSection, updateItem, addItem, moveItem])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !menuData) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            {tMenus('editor.loadFailed')}
          </p>
        </div>
      </div>
    )
  }

  const { menu, sections } = menuData

  return (
    <PermissionGate action={ACTIONS.MENU_EDIT}>
      <div className="flex flex-col h-full">
        <MenuEditorHeader
          menu={menu}
          onAddSection={() => setAddSectionOpen(true)}
        />

        <div className="flex flex-1 overflow-hidden">
          {/* Main editor area */}
          <div className="flex-1 overflow-y-auto p-6">
            {sections.length === 0 ? (
              <div className="flex items-center justify-center h-64 rounded-lg border border-dashed">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-4">
                    {tMenus('editor.emptyTitle')}
                  </p>
                  <button
                    onClick={() => setAddSectionOpen(true)}
                    className="text-sm text-primary hover:underline"
                  >
                    {tMenus('editor.emptyHint')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {sections.map((section) => (
                  <MenuSectionCard
                    key={section.id}
                    section={section}
                    menuId={menu.id}
                    restaurantId={menu.restaurantId}
                    menuDate={menu.menuDate ?? undefined}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Product selector panel */}
          <ProductSelectorPanel restaurantId={menu.restaurantId} />
        </div>

        <AddSectionDialog
          open={addSectionOpen}
          onOpenChange={setAddSectionOpen}
          menuId={menu.id}
        />
      </div>
    </PermissionGate>
  )
}
